import React, { useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  useMediaQuery,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  DeviceHub as ChannelsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Store as StoreIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  MoreHoriz as MoreIcon,
  Block as BlockIcon,
  School as SchoolIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import byteLogoFull from '../assets/byte-logo-full.svg';

import { useAuthContext } from '../context/AuthContext';
import { useOnboarding } from '../context/OnboardingContext';
import { useStoresQuery } from '../hooks/queries';
import { Settings } from '../features/settings/Settings';
import { StoreManagementWrapper } from './StoreManagement/StoreManagementWrapper';
import { StoreManagementDemo } from '../pages/StoreManagementDemo';
import { AllActivityPage, MyActivityPage } from '../pages/AuditHistory';
import { MobileSnoozeItems, MobileChannels, MobileHours, MobileMore } from './Mobile';
import { HelpCenter } from '../features/help';

const drawerWidth = 240;

// Desktop view types
type DesktopViewType =
  | 'store-hours'
  | 'channels'
  | 'items'
  | 'settings'
  | 'demo'
  | 'activity'
  | 'my-activity'
  | 'help';

// Mobile view types - task-focused
type MobileViewType =
  | 'snooze-items'
  | 'channels'
  | 'hours'
  | 'more'
  | 'activity'
  | 'my-activity'
  | 'help';

const desktopMenuItems = [
  { id: 'store-hours' as DesktopViewType, label: 'Store Management', icon: <StoreIcon /> },
  { id: 'activity' as DesktopViewType, label: 'Activity Log', icon: <HistoryIcon /> },
];

// Mobile bottom nav tabs (main navigation)
const mobileNavItems: { id: MobileViewType; label: string; icon: React.ReactNode }[] = [
  { id: 'snooze-items', label: 'Snooze Items', icon: <BlockIcon /> },
  { id: 'channels', label: 'Channels', icon: <ChannelsIcon /> },
  { id: 'hours', label: 'Hours', icon: <ScheduleIcon /> },
  { id: 'more', label: 'More', icon: <MoreIcon /> },
];

export function Layout() {
  const { user, logout } = useAuthContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  // Load stores for mobile views (desktop loads via StoreManagementWrapper)
  const { isLoading: storesLoading } = useStoresQuery();
  const { resetOnboarding } = useOnboarding();

  // Desktop view state
  const getCurrentDesktopView = (): DesktopViewType => {
    const path = location.pathname;
    if (path.startsWith('/stores') || path === '/') return 'store-hours';
    if (path === '/settings') return 'settings';
    if (path === '/demo') return 'demo';
    if (path === '/activity/me') return 'my-activity';
    if (path === '/activity') return 'activity';
    if (path === '/help') return 'help';
    return 'store-hours';
  };

  const [desktopView, setDesktopView] = useState<DesktopViewType>(getCurrentDesktopView());

  // Mobile view state - separate from URL routing
  const [mobileView, setMobileView] = useState<MobileViewType>('snooze-items');

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  const handleViewTutorial = () => {
    handleUserMenuClose();
    resetOnboarding();
  };

  const handleDesktopViewChange = (viewId: DesktopViewType) => {
    setDesktopView(viewId);
    switch (viewId) {
      case 'store-hours':
      case 'channels':
      case 'items':
        navigate('/stores');
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'demo':
        navigate('/demo');
        break;
      case 'activity':
        navigate('/activity');
        break;
      case 'my-activity':
        navigate('/activity/me');
        break;
      case 'help':
        navigate('/help');
        break;
    }
  };

  const handleMobileViewChange = (_event: React.SyntheticEvent, newValue: MobileViewType) => {
    setMobileView(newValue);
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="img"
            src={byteLogoFull}
            alt="Byte logo"
            sx={{ height: 28, width: 'auto' }}
          />
          <Typography variant="subtitle2" color="text.secondary">
            Store Control
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ flexGrow: 1 }}>
        {desktopMenuItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              selected={desktopView === item.id}
              onClick={() => handleDesktopViewChange(item.id)}
              sx={{
                '&.Mui-selected': {
                  bgcolor: 'rgba(102, 44, 139, 0.08)',
                  '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                    color: '#662C8B',
                  },
                },
                '& .MuiListItemIcon-root': {
                  color: 'text.secondary',
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            selected={desktopView === 'help'}
            onClick={() => handleDesktopViewChange('help')}
            sx={{
              '&.Mui-selected': {
                bgcolor: 'rgba(102, 44, 139, 0.08)',
                '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                  color: '#662C8B',
                },
              },
              '& .MuiListItemIcon-root': {
                color: 'text.secondary',
              },
            }}
          >
            <ListItemIcon>
              <HelpIcon />
            </ListItemIcon>
            <ListItemText primary="Help" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            selected={desktopView === 'settings'}
            onClick={() => handleDesktopViewChange('settings')}
            sx={{
              '&.Mui-selected': {
                bgcolor: 'rgba(102, 44, 139, 0.08)',
                '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                  color: '#662C8B',
                },
              },
              '& .MuiListItemIcon-root': {
                color: 'text.secondary',
              },
            }}
          >
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  // Render mobile content based on selected tab
  const renderMobileContent = () => {
    switch (mobileView) {
      case 'snooze-items':
        return <MobileSnoozeItems />;
      case 'channels':
        return <MobileChannels />;
      case 'hours':
        return <MobileHours />;
      case 'more':
        return <MobileMore onNavigate={(view) => setMobileView(view as MobileViewType)} />;
      case 'activity':
        return <AllActivityPage onBack={() => setMobileView('more')} />;
      case 'my-activity':
        return <MyActivityPage onBack={() => setMobileView('more')} />;
      case 'help':
        return <HelpCenter onBack={() => setMobileView('more')} />;
      default:
        return <MobileSnoozeItems />;
    }
  };

  // Mobile layout - clean, minimal design for MApp context
  if (isMobile) {
    // Calculate bottom nav height: 56px tabs + safe area + extra padding for curved screens
    const bottomNavHeight = 'calc(56px + env(safe-area-inset-bottom, 0px) + 16px)';

    return (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh', bgcolor: '#f8f9fa' }}
      >
        {/* User menu (accessible from More tab) */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleUserMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {user?.firstName} {user?.lastName}
            </Typography>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleViewTutorial}>
            <ListItemIcon>
              <SchoolIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="View Tutorial" />
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
        </Menu>

        {/* Main content - full height, no top bar */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            // Sub-pages (activity, my-activity) handle their own header, so no top padding
            pt:
              mobileView === 'activity' || mobileView === 'my-activity'
                ? 0
                : 'env(safe-area-inset-top, 0px)',
            pb: bottomNavHeight, // Bottom nav + safe area + extra padding
            overflow: 'auto',
            bgcolor: '#f8f9fa',
            minHeight: '100dvh',
          }}
        >
          {storesLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            renderMobileContent()
          )}
        </Box>

        {/* Bottom Navigation */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
            // Extra padding for curved screens + safe area
            pb: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          }}
        >
          <Tabs
            value={mobileView === 'activity' || mobileView === 'my-activity' ? 'more' : mobileView}
            onChange={handleMobileViewChange}
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 56,
              '& .MuiTab-root': {
                minHeight: 56,
                minWidth: 0,
                px: 0.5,
                textTransform: 'none',
                fontSize: '0.7rem',
                fontWeight: 500,
                lineHeight: 1.2,
                color: 'text.secondary',
                transition: 'color 0.2s ease',
              },
              '& .MuiTab-root.Mui-selected': {
                color: '#662C8B',
              },
              '& .MuiTab-iconWrapper': {
                mb: 0.5,
                '& svg': {
                  fontSize: '1.35rem',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#662C8B',
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            {mobileNavItems.map((item) => (
              <Tab
                key={item.id}
                value={item.id}
                label={item.label}
                icon={item.icon as React.ReactElement}
                iconPosition="top"
              />
            ))}
          </Tabs>
        </Box>
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
          boxShadow: 'none',
        }}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              {desktopView === 'settings'
                ? 'Settings'
                : desktopView === 'activity'
                  ? 'Activity Log'
                  : desktopView === 'my-activity'
                    ? 'My Activity'
                    : desktopView === 'help'
                      ? 'Help'
                      : 'Store Management'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              onClick={handleUserMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                borderRadius: 2,
                px: 1.5,
                py: 0.75,
                transition: 'background-color 0.2s',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {user?.orgDisplayName && (
                  <Chip
                    label={user.orgDisplayName}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(102, 44, 139, 0.1)',
                      color: 'primary.main',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      height: 24,
                    }}
                  />
                )}
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user?.firstName} {user?.lastName}
                </Typography>
              </Box>
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                {user?.firstName?.[0]}
                {user?.lastName?.[0]}
              </Avatar>
            </Box>
          </Box>
        </Toolbar>
        {/* Byte Gradient Bar */}
        <Box
          sx={{
            height: 3,
            width: '100%',
            background: 'linear-gradient(90deg, #662C8B 0%, #DE1D3B 50%, #FAA51F 100%)',
            opacity: 0.8,
          }}
        />
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={Boolean(anchorEl)}
        onClose={handleUserMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleUserMenuClose();
            handleDesktopViewChange('my-activity');
          }}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="My Activity" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleUserMenuClose();
            handleDesktopViewChange('settings');
          }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </MenuItem>
        <MenuItem onClick={handleViewTutorial}>
          <ListItemIcon>
            <SchoolIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View Tutorial" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </MenuItem>
      </Menu>

      <Box component="nav" sx={{ width: drawerWidth, flexShrink: 0 }}>
        <Drawer
          variant="permanent"
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: `calc(100% - ${drawerWidth}px)`,
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          maxWidth: '100%',
        }}
      >
        {/* Spacer accounts for AppBar height: Toolbar (64px) + gradient bar (3px) */}
        <Toolbar sx={{ minHeight: 64 }} />
        <Box sx={{ height: 3 }} />
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <Routes>
            <Route path="/" element={<Navigate to="/stores" replace />} />
            <Route path="/stores" element={<StoreManagementWrapper />} />
            <Route path="/stores/:storeId/:function" element={<StoreManagementWrapper />} />
            <Route path="/activity" element={<AllActivityPage />} />
            <Route path="/activity/me" element={<MyActivityPage />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/demo" element={<StoreManagementDemo />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
}
